export const useBaseAlertController = defineStore("baseAlertController", () => {
  const isOpen = ref(false);

  const title = ref("");
  const message = ref("");
  const isMessageComponent = ref(false);
  const buttonLabel = ref("Ok");

  type BaseAlertCloseReason = "ok" | "dismiss";
  let pendingResolve: ((reason: BaseAlertCloseReason) => void) | null = null;

  const open = (options?: {
    title?: string;
    message?: string;
    isMessageComponent?: boolean;
    buttonLabel?: string;
  }) => {
    isOpen.value = true;
    title.value = options?.title ?? "Alert";
    message.value = options?.message ?? "Are you sure you want to proceed?";
    isMessageComponent.value = options?.isMessageComponent ?? false;
    buttonLabel.value = options?.buttonLabel ?? "Ok";
  };

  const resolvePending = (reason: BaseAlertCloseReason) => {
    const resolver = pendingResolve;
    pendingResolve = null;
    resolver?.(reason);
  };

  const close = (reason: BaseAlertCloseReason = "dismiss") => {
    isOpen.value = false;
    resolvePending(reason);
  };

  const setOpen = (nextOpen: boolean) => {
    // `Dialog` can close itself (overlay click / ESC). We treat that as dismiss.
    if (isOpen.value === true && nextOpen === false) {
      isOpen.value = false;
      resolvePending("dismiss");
      return;
    }

    isOpen.value = nextOpen;
  };

  const openAsync = (options?: {
    title?: string;
    message?: string;
    isMessageComponent?: boolean;
    buttonLabel?: string;
  }) => {
    // If something is already pending, consider it dismissed.
    resolvePending("dismiss");
    open(options);

    return new Promise<BaseAlertCloseReason>((resolve) => {
      pendingResolve = resolve;
    });
  };

  return {
    isOpen,
    title,
    message,
    isMessageComponent,
    buttonLabel,
    open,
    openAsync,
    close,
    setOpen,
  };
});
